import { normalizeId, sameId } from '@vnetwork/margarita-shell/iam';
import { K8S_V2_BASE, clusterPath, clusterTabPath } from '../paths';

/** The search parameter that names a foreign workspace: `/services/kubernetes/v2/clusters?organization_id=<customer id>`. */
const ORG_PARAM = 'organization_id';

/** Why Create cluster is off in another organization's workspace (the backend refuses the POST: kubernetes#8). */
export const CREATE_IN_FOREIGN_REASON = "Creating a cluster in another organization isn't available yet.";

/** The organization a search string names; '' when it names none. */
export function organizationParam(search: string): string {
  return normalizeId(new URLSearchParams(search).get(ORG_PARAM));
}

/** The search that opens `organizationId`'s workspace; '' for the home workspace (`home`). */
export function workspaceSearch(organizationId: string, home: boolean): string {
  const id = normalizeId(organizationId);
  return home || !id ? '' : `?${ORG_PARAM}=${encodeURIComponent(id)}`;
}

export interface KubernetesWorkspaceOrg {
  /** The organization whose clusters the page lists; '' = the home workspace (the backend's default). */
  organizationId: string;
  /** Another organization's workspace is open. */
  foreign: boolean;
}

/**
 * Which workspace a Kubernetes page is in, from its search and the home organization. No parameter, or the home
 * organization itself, is the home workspace. While the home organization is not known yet a named organization counts
 * as foreign, so nothing that is foreign-only-unsafe (Create) is offered in the meantime.
 */
export function kubernetesWorkspaceOrg(search: string, homeOrganizationId: string | null | undefined): KubernetesWorkspaceOrg {
  const named = organizationParam(search);
  if (!named || sameId(named, homeOrganizationId)) return { organizationId: '', foreign: false };
  return { organizationId: named, foreign: true };
}

/** The links of the cluster pages; `search` (`workspaceSearch` of the open workspace, '' at home) keeps the workspace across them. */
export function clusterLinks(search: string) {
  return {
    list: `${K8S_V2_BASE}/clusters${search}`,
    vpcs: `${K8S_V2_BASE}/vpcs${search}`,
    cluster: (clusterId: string) => `${clusterPath(clusterId)}${search}`,
    tab: (clusterId: string, slug: string) => `${clusterTabPath(clusterId, slug)}${search}`,
  };
}

/** Whether a cluster can be created from the workspace that is open, and if not, the reason shown. */
export function createClusterRule(foreign: boolean): { enabled: boolean; reason: string | null } {
  return foreign ? { enabled: false, reason: CREATE_IN_FOREIGN_REASON } : { enabled: true, reason: null };
}
